import { DeleteOutlined, EditOutlined, EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Pagination, UsersTableRow } from '@app/models/user/UserResponse';
import { getUsersTableData } from '@app/api/user.api';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export const UsersTable: React.FC = () => {
  //const [filters, setFilters] = useState<{ [key: string]: string | null }>({});
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<Record<string, { label: string; value: string }[]> | null>(null);
  const [tableData, setTableData] = useState<{ data: UsersTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const lastFetchedRef = useRef(false);

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      getUsersTableData({ ...pagination, ...filters })
        .then((res) => {
          console.log(res);
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: res.pagination,
              loading: false,
            });
            setStatusOptions(res.statusOptions);
          }
        })
        .catch((error: any) => {
          notificationController.error({
            message: error.message,
          });
        });
    },
    [isMounted, filters],
  );

  useEffect(() => {
    if (!lastFetchedRef.current) {
      lastFetchedRef.current = true;
      fetch(initialPagination);
    }
  }, [fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      ...filters,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-user`, { state: { id } });
  };

  const handleEditRow = (id: number) => {
    navigate(`/edit-user`, { state: { id } });
  };

  const handleDeleteRow = (id: number) => {
    console.log(`Deleting row with id`, { state: { id } });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Users`}</span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'status',
      placeholder: 'status',
      type: 'dropdown',
      options: statusOptions || { statusOptions: [] },
    },
  ];

  const columns: ColumnsType<UsersTableRow> = [
    {
      title: 'Name',
      dataIndex: 'username',
      key: 'username',
      width: '30%',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '30%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>

            <Tooltip title="Edit">
              <Button
                type="text"
                onClick={() => handleEditRow(record.id)}
                icon={<EditOutlined />}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>

            <Tooltip title="Delete">
              <Popconfirm
                title="Are you sure to delete this user?"
                onConfirm={() => handleDeleteRow(record.id)}
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              >
                <Button type="text" danger icon={<DeleteOutlined />} style={{ color: 'red' }} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];
  return (
    <BaseCard
      id="basic-table"
      title="Users"
      padding="1.25rem"
      // extra={
      //   <BaseButtonsForm
      //     form={form}
      //     layout="inline"
      //     isFieldsChanged={fieldsChanged}
      //     setFieldsChanged={setFieldsChanged}
      //   >
      //     <BaseButtonsForm.Item name={'search'}>
      //       <Input
      //         size="middle"
      //         suffix={
      //           <Button
      //             icon={<SearchOutlined />}
      //             size="middle"
      //             type="text"
      //             onClick={() => {
      //               fetch(initialPagination);
      //             }}
      //           />
      //         }
      //         placeholder={'Search Users'}
      //       />
      //     </BaseButtonsForm.Item>
      //     <Button
      //       type="ghost"
      //       size="middle"
      //       icon={<ReloadOutlined />}
      //       onClick={() => {
      //         form.resetFields(['search']);
      //         fetch(initialPagination);
      //       }}
      //     />
      //   </BaseButtonsForm>
      // }
    >
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
